// <s-charge-button> wraps the real add-to-cart submit button.
// - On click it runs the cyan electric current around the button border ([data-charging]).
// - The current keeps running while <s-product-form> is busy talking to the cart.
// - Only when <s-product-form> confirms the cart update ("s-product-form:added") does the button
//   switch to its "Added to cart" state ([data-added]) and announce it to screen readers.
// - On "s-product-form:failed" the animation stops and the form's own error message is shown.
// The wrapped button keeps its native type/name, so form submission and cart behaviour are untouched.
class SChargeButton extends HTMLElement {
  #ac;
  #chargeTimer;
  #addedTimer;
  #button = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const form = this.closest('s-product-form');

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button, a') : null;
      if (!button || !this.contains(button)) return;
      if (button.getAttribute('aria-disabled') === 'true' && !button.hasAttribute('data-charging')) return;
      this.#button = button;
      this.#clearAdded();
      this.#charge(button);
    }, opts);

    form?.addEventListener('s-product-form:added', () => this.#showAdded(), opts);
    form?.addEventListener('s-product-form:failed', () => this.#stop(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#chargeTimer);
    clearTimeout(this.#addedTimer);
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #liveRegion() {
    return this.querySelector('[data-charge-status]');
  }

  #charge(button) {
    if (this.#reduceMotion) return;
    clearTimeout(this.#chargeTimer);
    // Restart the animation when clicked again mid-run.
    button.removeAttribute('data-charging');
    void button.offsetWidth;
    button.setAttribute('data-charging', '');
    // Minimum visible run; if the cart is still busy the "added"/"failed" event ends it instead.
    this.#chargeTimer = setTimeout(() => {
      if (this.closest('s-product-form')?.getAttribute('aria-busy') !== 'true') this.#stop();
    }, 1400);
  }

  #showAdded() {
    const button = this.#button || this.querySelector('button');
    if (!button) return;
    clearTimeout(this.#chargeTimer);
    button.removeAttribute('data-charging');
    button.setAttribute('data-added', '');
    const status = this.#liveRegion;
    if (status) status.textContent = status.dataset.addedMessage || '';
    clearTimeout(this.#addedTimer);
    this.#addedTimer = setTimeout(() => this.#clearAdded(), 2600);
  }

  #clearAdded() {
    clearTimeout(this.#addedTimer);
    this.querySelector('[data-added]')?.removeAttribute('data-added');
    const status = this.#liveRegion;
    if (status) status.textContent = '';
  }

  #stop() {
    clearTimeout(this.#chargeTimer);
    this.querySelector('[data-charging]')?.removeAttribute('data-charging');
  }
}

if (!customElements.get('s-charge-button')) {
  customElements.define('s-charge-button', SChargeButton);
}
